// src/components/MultiLocationMap.jsx
// MapLibre GL JS (مجاني 100%) + OSRM — محطات متصلة بالترتيب
import React, { useEffect, useRef, useCallback, useState } from "react";

// ── Lazy loader لـ MapLibre ──────────────────────────────────────────────────
let mlLoaded  = false;
let mlLoading = false;
let mlCallbacks = [];
let mlError   = null;

const loadMapLibre = (onSuccess, onError) => {
  if (mlLoaded) { onSuccess(); return; }
  if (mlError)  { onError(mlError); return; }
  mlCallbacks.push({ onSuccess, onError });
  if (mlLoading) return;
  mlLoading = true;

  const link = document.createElement("link");
  link.rel   = "stylesheet";
  link.href  = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css";
  document.head.appendChild(link);

  const script    = document.createElement("script");
  script.src      = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js";
  script.async    = true;
  script.onload   = () => {
    mlLoaded = true; mlLoading = false;
    mlCallbacks.forEach(({ onSuccess: cb }) => cb());
    mlCallbacks = [];
  };
  script.onerror  = () => {
    mlError = "Failed to load MapLibre GL"; mlLoading = false;
    mlCallbacks.forEach(({ onError: cb }) => cb(mlError));
    mlCallbacks = [];
  };
  document.head.appendChild(script);
};

const useMapLibre = () => {
  const [loaded, setLoaded] = useState(mlLoaded);
  const [error,  setError]  = useState(mlError);
  useEffect(() => {
    if (mlLoaded) { setLoaded(true); return; }
    const t = setTimeout(() => { if (!mlLoaded) setError("Map load timeout"); }, 10000);
    loadMapLibre(
      () => { clearTimeout(t); setLoaded(true); },
      (e) => { clearTimeout(t); setError(e); }
    );
    return () => clearTimeout(t);
  }, []);
  return { loaded, error };
};

// ── OSRM: مسار واحد متعدد المحطات بالترتيب ──────────────────────────────────
// stops = [[lat, lng], ...] مرتبة
const fetchOSRMSequential = async (stops) => {
  if (stops.length < 2) throw new Error("Need at least 2 stops");
  // OSRM يقبل lng,lat;lng,lat;...
  const coordStr = stops.map(([lat, lng]) => `${lng},${lat}`).join(";");
  const url = `https://router.project-osrm.org/route/v1/driving/${coordStr}?overview=full&geometries=geojson`;
  const res  = await fetch(url);
  const data = await res.json();
  if (data.code !== "Ok" || !data.routes?.length) throw new Error("No route");
  return data.routes[0].geometry.coordinates; // [[lng, lat], ...]
};

// ── SVG marker factory ────────────────────────────────────────────────────────
const makeMarkerEl = (color, label) => {
  const el = document.createElement("div");
  el.innerHTML = `
    <div style="
      width:32px;height:32px;border-radius:50% 50% 50% 0;
      background:${color};border:3px solid #fff;
      box-shadow:0 2px 6px rgba(0,0,0,.4);
      transform:rotate(-45deg);
      display:flex;align-items:center;justify-content:center;
    ">
      <span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:11px;">${label}</span>
    </div>`;
  return el;
};

const createLocationsControl = (onClick) => ({
  onAdd(map) {
    this._map = map;
    this._container = document.createElement("div");
    this._container.className = "maplibregl-ctrl maplibregl-ctrl-group";
    this._button = document.createElement("button");
    this._button.type = "button";
    this._button.title = "Show all locations";
    this._button.setAttribute("aria-label", "Show all locations");
    this._button.textContent = "↺";
    this._button.addEventListener("click", onClick);
    this._container.appendChild(this._button);
    return this._container;
  },
  onRemove() {
    this._button?.removeEventListener("click", onClick);
    this._container?.remove();
    this._map = undefined;
  },
});

// ── Coordinate parser — يقبل string, [lat,lng], أو [[string]] ───────────────
const parseCoord = (input) => {
  if (!input) return null;
  try {
    if (typeof input === "string") {
      const [lat, lng] = input.split(",").map((v) => parseFloat(v.trim()));
      return isNaN(lat) || isNaN(lng) ? null : [lat, lng];
    }
    if (Array.isArray(input)) {
      // [[string]] format من الكود القديم
      const val = input.length === 1 && typeof input[0] === "string" ? input[0] : input;
      if (typeof val === "string") {
        const [lat, lng] = val.split(",").map((v) => parseFloat(v.trim()));
        return isNaN(lat) || isNaN(lng) ? null : [lat, lng];
      }
      if (Array.isArray(val) && val.length === 2) {
        const [lat, lng] = val.map(Number);
        return isNaN(lat) || isNaN(lng) ? null : [lat, lng];
      }
    }
  } catch (e) { /* ignore */ }
  return null;
};

const normalizeList = (list) =>
  list.map(parseCoord).filter(Boolean);

const normalizeRouteStops = (stops = []) =>
  (Array.isArray(stops) ? stops : [])
    .map((stop) => {
      if (Array.isArray(stop) && stop.length >= 2) {
        const [lat, lng] = stop.map(Number);
        return isNaN(lat) || isNaN(lng) ? null : [lat, lng];
      }

      if (stop && typeof stop === "object") {
        const lat = Number(stop.lat ?? stop.latitude);
        const lng = Number(stop.lng ?? stop.lon ?? stop.longitude);
        return isNaN(lat) || isNaN(lng) ? null : [lat, lng];
      }

      return parseCoord(stop);
    })
    .filter(Boolean);

// ألوان المحطات بالترتيب
const STOP_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#a855f7"];

// ─────────────────────────────────────────────────────────────────────────────
const MultiLocationMap = ({
  fromCoords = [],
  toCoords = [],
  routeStops = [],
  height = "300px",
}) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const isMounted    = useRef(true);
  const initialized  = useRef(false);
  const { loaded, error } = useMapLibre();

  // ── بناء قائمة المحطات المرتبة وإرسالها لـ OSRM ─────────────────────────
  const drawAll = useCallback(async (map) => {
    if (!map || !isMounted.current) return;
    const ml = window.maplibregl;

    // ── تنظيف layers/sources القديمة ─────────────────────────
    const style = map.getStyle();
    (style?.layers ?? [])
      .filter((l) => l.id === "route" || l.id === "route-outline")
      .forEach((l) => {
        try { map.removeLayer(l.id); } catch (_) {
          return;
        }
      });
    if (map.getSource("route")) {
      try { map.removeSource("route"); } catch (_) {
        return;
      }
    }

    // ── تنظيف markers القديمة ────────────────────────────────
    (mapRef._markers ?? []).forEach((m) => m.remove());
    mapRef._markers = [];

    // ── تجهيز المحطات ────────────────────────────────────────
    const normalizedRouteStops = normalizeRouteStops(routeStops);
    const fromList = normalizeList(fromCoords);
    const toList = normalizeList(toCoords);

    const orderedStops =
      normalizedRouteStops.length > 0
        ? normalizedRouteStops.map((coord, index) => ({
            coord,
            index,
            type: "route",
          }))
        : (() => {
            const maxLen = Math.max(fromList.length, toList.length);
            const stops = [];
            for (let i = 0; i < maxLen; i++) {
              if (fromList[i]) stops.push({ coord: fromList[i], type: "from", index: i });
              if (toList[i]) stops.push({ coord: toList[i], type: "to", index: i });
            }
            return stops;
          })();

    if (orderedStops.length === 0) {
      mapRef._locationsBounds = null;
      map.setCenter([10.4515, 51.1657]);
      map.setZoom(5);
      return;
    }

    // ── إضافة Markers ────────────────────────────────────────
    orderedStops.forEach(({ coord, type, index }, orderIndex) => {
      const color = STOP_COLORS[orderIndex % STOP_COLORS.length];
      const label = String(orderIndex + 1);

      const el = makeMarkerEl(color, label);
      const marker = new ml.Marker({ element: el })
        .setLngLat([coord[1], coord[0]])
        .setPopup(
          new ml.Popup({ offset: 25 }).setText(
            normalizedRouteStops.length > 0
              ? `Stop ${orderIndex + 1}`
              : type === "from"
                ? `Pickup ${index + 1}`
                : `Delivery ${index + 1}`
          )
        )
        .addTo(map);
      mapRef._markers.push(marker);
    });

    // ── حساب Bounds لكل المحطات ──────────────────────────────
    const allLngLat = orderedStops.map(({ coord }) => [coord[1], coord[0]]);
    const bounds = allLngLat.reduce(
      (b, c) => b.extend(c),
      new ml.LngLatBounds(allLngLat[0], allLngLat[0])
    );
    mapRef._locationsBounds = bounds;

    // ✅ دالة مساعدة لعمل fitBounds بطريقة آمنة
    const safeFitBounds = (b, options = {}) => {
      if (!b || !isMounted.current) return;
      try {
        map.fitBounds(b, {
          padding: options.padding ?? 60,
          maxZoom: options.maxZoom ?? 14,
          duration: options.duration ?? 800,
        });
      } catch (e) {
        console.warn("fitBounds error:", e);
      }
    };

    // ✅ fitBounds أولي فوراً
    safeFitBounds(bounds);

    // ✅ إعادة fitBounds بعد tick للتأكد من الأبعاد
    setTimeout(() => {
      if (!isMounted.current) return;
      map.resize();
      safeFitBounds(bounds);
    }, 200);

    if (orderedStops.length < 2) return;

    // ── OSRM: مسار واحد متعدد المحطات ────────────────────────
    const coords = orderedStops.map(({ coord }) => coord);

    try {
      const routeCoords = await fetchOSRMSequential(coords);
      if (!isMounted.current) return;

      const geojson = {
        type: "Feature",
        geometry: { type: "LineString", coordinates: routeCoords },
      };

      map.addSource("route", { type: "geojson", data: geojson });

      map.addLayer({
        id: "route-outline",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#fff", "line-width": 8, "line-opacity": 0.35 },
      });

      map.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#ff6b00", "line-width": 5, "line-opacity": 0.9 },
      });

      // fitBounds على المسار الفعلي
      const routeBounds = routeCoords.reduce(
        (b, c) => b.extend(c),
        new ml.LngLatBounds(routeCoords[0], routeCoords[0])
      );

      safeFitBounds(routeBounds);

      // ✅ إعادة fitBounds تاني بعد ما الـ route يتضاف
      setTimeout(() => {
        if (!isMounted.current) return;
        map.resize();
        safeFitBounds(routeBounds);
      }, 200);

    } catch (err) {
      if (!isMounted.current) return;
      console.warn("OSRM failed, drawing straight lines:", err.message);

      const straightCoords = orderedStops.map(({ coord }) => [coord[1], coord[0]]);
      const geojson = {
        type: "Feature",
        geometry: { type: "LineString", coordinates: straightCoords },
      };
      map.addSource("route", { type: "geojson", data: geojson });
      map.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#ff6b00",
          "line-width": 4,
          "line-opacity": 0.7,
          "line-dasharray": [2, 2],
        },
      });

      // ✅ fitBounds حتى لو OSRM فشل
      safeFitBounds(bounds);
    }
  }, [fromCoords, toCoords, routeStops]);

  // ── Init ───────────────────────────────────────────────────────────────────
  const initMap = useCallback(() => {
    if (!loaded || !containerRef.current) return;
    if (initialized.current) return;
    initialized.current = true;

    const ml  = window.maplibregl;
    const map = new ml.Map({
      container: containerRef.current,
      style:  "https://tiles.openfreemap.org/styles/liberty",
      center: [10.4515, 51.1657],
      zoom:   5,
      attributionControl: false,
    });

    map.addControl(new ml.NavigationControl(), "top-right");
    map.addControl(new ml.FullscreenControl(), "top-right");
    map.addControl(
      createLocationsControl(() => {
        if (mapRef._locationsBounds) {
          map.fitBounds(mapRef._locationsBounds, { padding: 60, maxZoom: 13 });
        }
      }),
      "top-right"
    );
    map.addControl(new ml.ScaleControl({ unit: "metric" }), "bottom-left");
    map.addControl(
      new ml.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
      }),
      "top-right"
    );
    map.addControl(new ml.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current  = map;
    mapRef._markers = [];

    // ✅ ResizeObserver — يحل مشكلة offcanvas/accordion في mobile
    const resizeObs = new ResizeObserver(() => {
      if (containerRef.current?.offsetWidth > 0 && isMounted.current) {
        map.resize();
        // ✅ إعادة fitBounds بعد resize
        if (mapRef._locationsBounds) {
          try {
            map.fitBounds(mapRef._locationsBounds, {
              padding: 60,
              maxZoom: 14,
              duration: 400,
            });
          } catch (_) {
            return;
          }
        }
      }
    });

    resizeObs.observe(containerRef.current);
    mapRef._resizeObserver = resizeObs;

    map.on("load", () => {
      if (!isMounted.current) return;
      drawAll(map);
    });
  }, [loaded, drawAll]);

  // ── Update عند تغيير الـ coords ───────────────────────────────────────────
  const updateAll = useCallback(() => {
    if (!initialized.current || !mapRef.current) return;
    if (!mapRef.current.isStyleLoaded()) {
      mapRef.current.once("load", () => drawAll(mapRef.current));
    } else {
      drawAll(mapRef.current);
    }
  }, [drawAll]);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (loaded && !initialized.current) initMap();
  }, [loaded, initMap]);

  useEffect(() => {
    if (initialized.current) updateAll();
  }, [fromCoords, toCoords, routeStops]); // eslint-disable-line

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      mapRef._resizeObserver?.disconnect();
      mapRef._markers?.forEach((m) => m.remove());
      mapRef.current?.remove();
      mapRef.current      = null;
      mapRef._locationsBounds = null;
      initialized.current = false;
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (error) {
    return <div className="text-danger p-3">⚠️ {error}</div>;
  }

  if (!loaded) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading map...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width:        "100%",
        borderRadius: "10px",
        overflow:     "hidden",
        border:       "1px solid #ddd",
      }}
      aria-label="Map showing all pickup and delivery locations"
    />
  );
};

export { MultiLocationMap };
export default MultiLocationMap;